import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { Button } from "@/components/Button/Button";
import { Image } from "@/components/Image/Image";
import { ReservationType } from "@/features/bookings/types/types";
import { useBannersPlacementQuery } from "@/hooks/useBannersPlacement";
import { BannersPlacementSlug } from "@/types/enums";

type Props = {
  type: "flights" | "hotels";
  reservationType: ReservationType;
};

const config = {
  flights: {
    bannerSlug: BannersPlacementSlug.FLIGHTS_EMPTY_STATE,
    to: "/flights/search" as const,
  },
  hotels: {
    bannerSlug: BannersPlacementSlug.HOTELS_EMPTY_STATE,
    to: "/hotels/search" as const,
  },
};

export function BookingsEmptyState({ type, reservationType }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { bannerSlug, to } = config[type];

  const emptyStateTitles: Record<ReservationType, string> = {
    [ReservationType.UPCOMING]: intl.formatMessage({
      defaultMessage: "No upcoming bookings!",
      description: "Title shown when user has no upcoming bookings",
    }),
    [ReservationType.PAST]: intl.formatMessage({
      defaultMessage: "No past bookings!",
      description: "Title shown when user has no past bookings",
    }),
    [ReservationType.REFUNDED]: intl.formatMessage({
      defaultMessage: "No cancelled bookings!",
      description: "Title shown when user has no cancelled bookings",
    }),
  };

  const { data: placement, isPending } = useBannersPlacementQuery(bannerSlug);
  const hasBanners =
    !isPending &&
    placement?.isEnabled === true &&
    (placement.banners.length ?? 0) > 0;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="size-60">
        <Image src="/illustrations/tripsEmptyState.svg" alt="" aria-hidden />
      </div>

      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="text-lg font-semibold text-text-primary">
          {emptyStateTitles[reservationType]}
        </h2>
        <p className="text-base text-text-secondary">
          {intl.formatMessage({
            defaultMessage:
              "Explore our best offers or get inspired for your next booking.",
            description: "Subtitle shown when user has no bookings",
          })}
        </p>
      </div>

      <Button
        variant="contained"
        startIcon={<MagnifyingGlassIcon size={20} />}
        onClick={() => navigate({ to })}
      >
        {intl.formatMessage({
          defaultMessage: "Explore",
          description: "CTA button on empty bookings state to explore offers",
        })}
      </Button>

      {hasBanners && (
        <div className="mt-2 w-full">
          <h2 className="mb-3 text-lg font-semibold text-text-title">
            {intl.formatMessage({
              defaultMessage: "Offers",
              description:
                "Section title for promotions on empty bookings state",
            })}
          </h2>
          <BannersPlacement slug={bannerSlug} />
        </div>
      )}
    </div>
  );
}
